import { useEffect, useId, useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { CaretDown, DownloadSimple } from '@phosphor-icons/react'

/**
 * Botón reutilizable de descarga con una acción principal y un menú de opciones.
 * Sirve para descargar distintos archivos o formatos en cualquier módulo.
 * El componente muestra las opciones; cada callback implementa su descarga.
 *
 * `onPrimaryClick` ejecuta la descarga principal y `disabled` deshabilita ambos botones.
 * Cada elemento de `items` acepta label y onClick; key, description, icon y disabled
 * son opcionales. El menú se cierra al seleccionar una opción habilitada.
 *
 * Ejemplo:
 * <DownloadOptionsButton
 *   label="Descargar"
 *   onPrimaryClick={downloadPdf}
 *   items={[
 *     { key: 'pdf', label: 'Archivo PDF', onClick: downloadPdf },
 *     { key: 'csv', label: 'Archivo CSV', description: 'Datos en tabla.', onClick: downloadCsv }
 *   ]}
 * />
 */
export default function DownloadOptionsButton({
  label = 'Descargar',
  onPrimaryClick,
  items = [],
  disabled = false
}) {
  const [anchor, setAnchor] = useState(null)
  const menuId = useId()

  useEffect(() => {
    if (disabled) setAnchor(null)
  }, [disabled])

  return (
    <>
      <div className="download-options-button">
        <button type="button" onClick={onPrimaryClick} disabled={disabled}>
          <DownloadSimple size={17} aria-hidden="true" />
          {label}
        </button>
        <button
          type="button"
          aria-label="Más opciones de descarga"
          aria-haspopup="menu"
          aria-controls={anchor ? menuId : undefined}
          aria-expanded={Boolean(anchor)}
          disabled={disabled}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <CaretDown size={16} aria-hidden="true" />
        </button>
      </div>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor) && !disabled}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { className: 'download-options-menu' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key || item.label}
            disabled={disabled || item.disabled}
            onClick={() => {
              setAnchor(null)
              item.onClick()
            }}
          >
            <span className="download-options-menu__icon">
              {item.icon || <DownloadSimple size={18} />}
            </span>
            <span>
              {item.label}
              {item.description && <small>{item.description}</small>}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
